// oxlint-disable typescript-eslint/prefer-readonly-parameter-types -- Storage is an external mutable DOM interface
// Cross-page persistence for a screenshot the widget asked the host page to
// take.
//
// The widget closes so the user can bring the screen they want to show into
// view – which may mean navigating to another page of the host site. A full
// navigation reloads the snippet, so the "screenshot this page" bar has to
// come back on the new page or the ask silently vanishes. The marker in
// `sessionStorage` is what tells the next page load to remount the bar and
// which thread the capture belongs to.
//
// `sessionStorage` (not `localStorage`) is deliberate: the ask belongs to one
// tab and must not leak into a second tab or survive the tab closing.

import type { UserReplayContext } from "./replay-session";

import { CAPTURE_MARKER_TTL_MS } from "./replay-session";

export interface ScreenshotSession {
	// The thread turn that asked for the screenshot, so the capture lands on
	// the card that requested it and nowhere else.
	readonly context: UserReplayContext | undefined;
	// The project whose widget asked. Another project's mount drops the ask.
	readonly projectId: string | undefined;
	readonly startedAt: number;
}

const MARKER_KEY = "weloop-screenshot-request";

const isRecord = (raw: unknown): raw is Readonly<Record<string, unknown>> =>
	typeof raw === "object" && raw !== null;

const parseContext = (raw: unknown): UserReplayContext | undefined => {
	if (!isRecord(raw)) {
		return undefined;
	}
	const { feedbackId, messageId } = raw;
	const context: UserReplayContext = {
		...(typeof feedbackId === "string" ? { feedbackId } : {}),
		...(typeof messageId === "string" ? { messageId } : {}),
	};
	return Object.keys(context).length === 0 ? undefined : context;
};

/** The tab's session storage, or undefined on a host page that blocks it. */
export const resolveSessionStorage = (): Storage | undefined => {
	try {
		return window.sessionStorage;
	} catch {
		return undefined;
	}
};

/** Read the pending screenshot ask, if this tab has one. */
export const readScreenshotSession = (
	storage: Storage,
): ScreenshotSession | undefined => {
	try {
		const raw = storage.getItem(MARKER_KEY);
		if (raw === null) {
			return undefined;
		}
		const parsed: unknown = JSON.parse(raw);
		if (!isRecord(parsed)) {
			return undefined;
		}
		const { projectId, startedAt } = parsed;
		if (typeof startedAt !== "number") {
			return undefined;
		}
		// A duplicated or restored tab copies the marker; past the expiry it is
		// a leftover, not an ask the user is still making.
		if (Date.now() - startedAt > CAPTURE_MARKER_TTL_MS) {
			storage.removeItem(MARKER_KEY);
			return undefined;
		}
		return {
			context: parseContext(parsed["context"]),
			projectId: typeof projectId === "string" ? projectId : undefined,
			startedAt,
		};
	} catch {
		// A host page can block storage entirely (sandboxed iframe, privacy
		// mode). The ask then lives for the current page only.
		return undefined;
	}
};

/** Record a new ask, replacing any earlier one still on record. */
export const beginScreenshotSession = (
	storage: Storage,
	session: ScreenshotSession,
): void => {
	try {
		storage.setItem(MARKER_KEY, JSON.stringify(session));
	} catch {
		// Best effort – the bar still works on this page.
	}
};

/** Drop the pending ask. */
export const clearScreenshotSession = (storage: Storage): void => {
	try {
		storage.removeItem(MARKER_KEY);
	} catch {
		// Best effort.
	}
};
